import { RadioGroup } from '@headlessui/react'
import React, { useEffect, useState } from 'react'
import Spin from '../../components/Spin'
import i18n from '../../i18n'
import { apiGet } from '../../libs/apiHelper'
import { useAppContext } from '../../libs/context'
import { dateToString } from '../../libs/date'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const SelectCampaign = () => {
  const {
    campaignsTypes,
    setCampaignsTypes,
    selectedCampaign,
    setSelectedCampaign,
    onStepClick,
    stepsData,
    setStepsData
  } = useAppContext()

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const getCampaignsTypes = async () => {
    setLoading(true)
    setErrorMessage('')
    try {
      const req = await apiGet('/campaigns-types')
      const cleanData = req.map((item) => {
        const parseText = typeof item.elements === 'string' 
          ? JSON.parse(item.elements) 
          : item.elements
        let keys = Object.keys(parseText).find((key) => key.includes('init-1'))
        let text
        if (!keys) {
          keys = Object.keys(parseText).find((key) => key.includes('initSMS'))
          text = parseText[keys]
        } else {
          if (parseText[keys].RCSMessage) {
            text = parseText[keys].RCSMessage.textMessage
          } else if (parseText[keys].contentMessage) {
            text = parseText[keys].contentMessage.text
          } else {
            text = parseText[keys].messageText
          }
        }
        item.text = text?.length > 20 ? `${text.substr(0, 50)}...` : text
        const parseDate = dateToString(item.timestamp)
        item.date = parseDate.dayMin
        item.time = parseDate.time
        item.initElement = keys
        item.schema = item.schema ? item.schema : 'basic'
        return item
      })
      setCampaignsTypes(cleanData)
    } catch (err) {
      console.log('error', err)
      setErrorMessage(i18n.t('error_loading_templates') || 'Error loading templates')
    }
    setLoading(false)
  }

  const handleOnChange = (campaign) => {
    setSelectedCampaign(campaign)
    // Marcar el paso 1 como completado
    const _stepsData = [...stepsData]
    _stepsData.find(({ id }) => id === 1).completed = true
    setStepsData(_stepsData)
  }

  const handleNextClick = () => {
    if (!selectedCampaign) {
      return
    }
    onStepClick({
      id: 2,
      href: 'upload'
    }, true) // forceNavigation = true porque ya validamos
  }

  useEffect(() => {
    if (!campaignsTypes.length) {
      getCampaignsTypes()
    }
  }, [])

  const isButtonDisabled = !selectedCampaign

  return (
    <div className="py-6">
      {loading ? (
        <div className="flex justify-center my-16">
          <Spin type="lightBg" />
        </div>
      ) : errorMessage ? (
        <div className="w-full max-w-xl mx-auto">
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{errorMessage}</h3>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={getCampaignsTypes}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-dark hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-dark"
            >
              {i18n.t('retry') || 'Retry'}
            </button>
          </div>
        </div>
      ) : campaignsTypes.length === 0 ? (
        <div className="w-full max-w-xl mx-auto text-center py-12">
          <ExclamationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {i18n.t('no_templates') || 'No templates available'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {i18n.t('create_template_first') || 'Create a template first to start a campaign'}
          </p>
        </div>
      ) : (
        <div className="w-full max-w-xl mx-auto">
          {/* Header con instrucciones */}
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900">
              {i18n.t('select_template_title') || 'Select a template'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {i18n.t('select_template_description') || 'Choose the template you want to use for this campaign'}
            </p>
          </div>

          <RadioGroup value={selectedCampaign} onChange={handleOnChange}>
            <RadioGroup.Label className="sr-only">
              {i18n.t('campaign')}
            </RadioGroup.Label>
            <div className="space-y-4">
              {campaignsTypes.map((campaign) => (
                <RadioGroup.Option
                  key={campaign.name}
                  value={campaign}
                  className={({ checked, active }) => {
                    return classNames(
                      checked ? 'border-blue-dark ring-2 ring-blue-dark' : 'border-gray-300',
                      active ? 'ring-2 ring-blue-dark' : '',
                      'relative block bg-white border rounded-lg shadow-sm px-6 py-4 cursor-pointer sm:flex sm:justify-between focus:outline-none transition-all duration-150 hover:border-gray-400'
                    )
                  }}
                >
                  {({ active, checked }) => (
                    <>
                      <span className="flex items-center">
                        <span className="text-sm flex flex-col">
                          <RadioGroup.Label
                            as="span"
                            className={classNames(
                              'font-medium',
                              checked ? 'text-blue-dark' : 'text-grey-blue'
                            )}
                          >
                            {campaign.name}
                          </RadioGroup.Label>
                          <RadioGroup.Description
                            as="span"
                            className="text-gray-500"
                          >
                            <span className="block sm:inline">
                              {campaign.text}
                            </span>
                          </RadioGroup.Description>
                        </span>
                      </span>
                      <RadioGroup.Description
                        as="span"
                        className="mt-2 flex text-sm sm:mt-0 sm:flex-col sm:ml-4 sm:text-right"
                      >
                        <span className="font-medium text-grey-blue">
                          {campaign.date}
                        </span>
                        <span className="ml-1 text-gray-500 sm:ml-0">
                          {campaign.time}
                        </span>
                      </RadioGroup.Description>
                      {/* Checkmark indicator */}
                      {checked && (
                        <CheckCircleIcon 
                          className="absolute top-4 right-4 h-6 w-6 text-blue-dark" 
                          aria-hidden="true" 
                        />
                      )}
                      <span
                        className={classNames(
                          active ? 'border' : 'border-2',
                          checked ? 'border-blue-dark' : 'border-transparent',
                          'absolute -inset-px rounded-lg pointer-events-none'
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>

          {/* Área de acción - siempre visible */}
          <div className={classNames(
            'mt-6 p-4 rounded-lg border-2 transition-all duration-200',
            selectedCampaign 
              ? 'bg-green-50 border-green-300' 
              : 'bg-gray-50 border-gray-200'
          )}>
            {selectedCampaign ? (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                  <span className="text-sm text-green-800">
                    <span className="font-semibold">{selectedCampaign.name}</span> {i18n.t('selected') || 'selected'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleNextClick}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
                >
                  {i18n.t('continue') || 'Continuar'}
                  <svg className="ml-2 -mr-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-sm text-gray-500">
                  {i18n.t('select_template_to_continue') || 'Select a template above to continue'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SelectCampaign
